import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { Facade, DraggerType } from "@/drawing";
import type { TypedInputSpec } from "@/drawing/draggers/types";
import DraggerMenuState from "@/stores/DraggerMenuState";
import MeasurementKeypadState from "@/stores/MeasurementKeypadState";
import Settings from "@/stores/Settings";

/**
 * Resolves the one-line hint shown at the bottom of the drawing tool: what the
 * user is expected to do right now, given the active tool and how far into its
 * gesture they are.
 *
 * Everything here is read from the drawing engine through subscriptions rather
 * than polled: the Facade is not reactive, so the refs below are the reactive
 * mirror the template watches.
 */

/** Hint shown while a tool is active but nothing is in progress yet. */
const IDLE_HINTS: Partial<Record<DraggerType, string>> = {
  [DraggerType.Freehand]: "canvas:tools.hints.freehand",
  [DraggerType.Line]: "canvas:tools.hints.line",
  [DraggerType.Polyline]: "canvas:tools.hints.polyline",
  [DraggerType.Rectangle]: "canvas:tools.hints.rectangle",
  [DraggerType.Ellipse]: "canvas:tools.hints.ellipse",
  [DraggerType.Dimension]: "canvas:tools.hints.dimension",
  [DraggerType.Text]: "canvas:tools.hints.text",
  [DraggerType.Handwriting]: "canvas:tools.hints.handwriting",
  [DraggerType.StylusKeyboard]: "canvas:tools.hints.stylusKeyboard",
  [DraggerType.Eraser]: "canvas:tools.hints.eraser",
  [DraggerType.Select]: "canvas:tools.hints.select",
  [DraggerType.Block]: "canvas:tools.hints.block",
  [DraggerType.ZoomWindow]: "canvas:tools.hints.zoomWindow",
  [DraggerType.Origin]: "canvas:tools.hints.origin",
};

export function useToolHint() {
  const activeType = ref<DraggerType>(DraggerType.None);
  /** i18n key of the step a base-point tool (copy/move/rotate/scale) waits for. */
  const stepPrompt = ref<string | null>(null);
  /** A point A is fixed and the tool waits for the next one. */
  const hasPendingPoint = ref(false);
  /**
   * What the active tool offers to the numeric keypad right now — null when
   * typing a value makes no sense (e.g. line/rectangle/ellipse outside
   * "point par point" mode). Same source TypedInputPanel reads.
   */
  const typedInputSpec = ref<TypedInputSpec | null>(null);
  /**
   * A sequential tool (polyline) already has at least one vertex: the draw
   * tools set stepId from the vertex they measure from, and leave it empty
   * until one exists.
   */
  const hasDraftSegment = computed(() => !!typedInputSpec.value?.stepId);
  const isDynamicBlockSelected = ref(false);

  let unsubscribeActive: (() => void) | undefined;
  let unsubscribeSelection: (() => void) | undefined;
  let unsubscribeDragger: (() => void)[] = [];
  let initCheckInterval: ReturnType<typeof setInterval> | undefined;

  function subscribeToDragger() {
    for (const unsubscribe of unsubscribeDragger) unsubscribe();
    unsubscribeDragger = [];
    stepPrompt.value = null;
    hasPendingPoint.value = false;
    typedInputSpec.value = null;

    if (!Facade.isInitialized) return;
    activeType.value = Facade.tools.currentType;

    const dragger = Facade.tools.current;
    if (!dragger) return;

    stepPrompt.value = dragger.getStepPrompt();
    hasPendingPoint.value = dragger.hasPendingPlacement();
    typedInputSpec.value = dragger.getTypedInputSpec();

    unsubscribeDragger.push(
      dragger.onStepPromptChange((prompt) => {
        stepPrompt.value = prompt;
      }),
      dragger.onPendingPlacementChange((pending) => {
        hasPendingPoint.value = pending;
      }),
      dragger.onTypedInputChange((spec) => {
        typedInputSpec.value = spec;
      }),
    );
  }

  function updateSelection() {
    if (!Facade.isInitialized) {
      isDynamicBlockSelected.value = false;
      return;
    }
    isDynamicBlockSelected.value =
      Facade.selection.handles.length === 1 &&
      Facade.dynBlocks.canEditSelected();
  }

  function trySubscribe(): boolean {
    if (!Facade.isInitialized) return false;
    unsubscribeActive = Facade.tools.onActiveChange(subscribeToDragger);
    unsubscribeSelection = Facade.selection.onChange(updateSelection);
    subscribeToDragger();
    updateSelection();
    return true;
  }

  onMounted(() => {
    // The Facade is initialized asynchronously by the canvas view — poll until
    // ready, same pattern as SelectionMenu.
    if (!trySubscribe()) {
      initCheckInterval = setInterval(() => {
        if (trySubscribe() && initCheckInterval) {
          clearInterval(initCheckInterval);
          initCheckInterval = undefined;
        }
      }, 100);
    }
  });

  onBeforeUnmount(() => {
    if (initCheckInterval) clearInterval(initCheckInterval);
    unsubscribeActive?.();
    unsubscribeSelection?.();
    for (const unsubscribe of unsubscribeDragger) unsubscribe();
    unsubscribeDragger = [];
  });

  // Tool changes made from the toolbar also go through onActiveChange, but the
  // toolbar can re-select the same DraggerType with a different config (a new
  // block, a new text): re-read the dragger so its fresh state is mirrored.
  watch(() => DraggerMenuState.activeDragger.name, subscribeToDragger);

  // Same availability rule as TypedInputPanel: the pad is open and the active
  // tool currently offers something to type.
  const isKeypadUsable = computed(
    () => MeasurementKeypadState.isOpen && typedInputSpec.value !== null,
  );

  /**
   * i18n key of the hint to display, most specific state first: an in-progress
   * gesture always beats the "how do I start this tool" hint it came from.
   */
  const hintKey = computed<string | null>(() => {
    if (!Settings.toolHintsOn) return null;

    // Copy/move/rotate/scale: the engine already names the step it waits for.
    if (stepPrompt.value) return stepPrompt.value;

    if (isKeypadUsable.value) return "canvas:tools.hints.keypad";

    if (activeType.value === DraggerType.Polyline) {
      if (hasDraftSegment.value) return "canvas:tools.hints.polylineInProgress";
    } else if (hasPendingPoint.value) {
      return "canvas:tools.hints.secondPoint";
    }

    if (
      activeType.value === DraggerType.Select &&
      isDynamicBlockSelected.value
    ) {
      return "canvas:tools.hints.dynamicBlock";
    }

    return IDLE_HINTS[activeType.value] ?? null;
  });

  return { hintKey };
}
